import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, FileDown, Printer } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";

type ReportAnswer = {
  id: string;
  status: "conforme" | "ressalva" | "nao_conforme";
  observation: string | null;
  severity: "leve" | "moderada" | "grave" | "gravissima";
  section: string;
  question: string;
  sort_order: number;
};

type ReportNC = {
  id: string;
  description: string;
  severity: "leve" | "moderada" | "grave" | "gravissima";
  status: "aberta" | "em_tratamento" | "resolvida";
  responsible: string | null;
  deadline: string | null;
  created_at: string;
};

type InspectionReport = {
  inspection: {
    id: string;
    vehicle_id: string;
    result: "apto" | "apto_ressalva" | "nao_apto";
    notes: string | null;
    created_at: string;
    report_number: string;
    identifier: string;
    plate: string | null;
    brand: string | null;
    model: string | null;
    year: number | null;
    odometer: number | null;
    responsible_name: string | null;
    operational_status: string;
    unit_id: string | null;
    unit_name: string | null;
    unit_code: string | null;
    vehicle_type: string;
    inspector_name: string;
    inspector_email: string;
    meter_type: "km" | "horimetro" | null;
    meter_value: number | null;
    meter_captured_at: string | null;
  };
  answers: ReportAnswer[];
  photos: { id: string; data_url: string | null; url: string | null; mime_type: string | null; byte_size: number; created_at: string }[];
  signature: { id: string; data_url: string; created_at: string } | null;
  nonconformities: ReportNC[];
  audit: { action: string; details: string | null; created_at: string; user_name: string | null }[];
  timestamp_standard: string;
};

const resultLabel: Record<string, string> = {
  apto: "APTO",
  apto_ressalva: "APTO COM RESSALVA",
  nao_apto: "NÃO APTO"
};

const answerLabel: Record<string, string> = {
  conforme: "Conforme",
  ressalva: "Ressalva",
  nao_conforme: "Não conforme"
};

const severityLabel: Record<string, string> = {
  leve: "Leve",
  moderada: "Moderada",
  grave: "Grave",
  gravissima: "Gravíssima"
};

const operationalLabel: Record<string, string> = {
  liberado: "Liberado",
  liberado_ressalva: "Liberado com ressalva",
  em_manutencao: "Em manutenção",
  inativo: "Inativo"
};

function formatDateTime(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const d = new Date(normalized);
  return Number.isNaN(d.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Fortaleza",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      }).format(d) + " BRT";
}

export function InspectionReportPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState<InspectionReport | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    api<InspectionReport>(`inspections/${id}/report`)
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : "Não foi possível carregar o relatório."));
  }, [id]);

  const stats = useMemo(() => {
    const answers = report?.answers ?? [];
    return {
      total: answers.length,
      compliant: answers.filter((a) => a.status === "conforme").length,
      warnings: answers.filter((a) => a.status === "ressalva").length,
      noncompliant: answers.filter((a) => a.status === "nao_conforme").length
    };
  }, [report]);

  if (error) {
    return (
      <section className="report-page">
        <div className="alert error">{error}</div>
        <button className="secondary" onClick={() => navigate("/inspecoes")}><ArrowLeft size={17} /> Voltar às inspeções</button>
      </section>
    );
  }

  if (!report) return <div className="center-page report-loading">Carregando relatório...</div>;

  const { inspection, answers, photos, signature, nonconformities, audit } = report;
  const conformity = stats.total ? Math.round((stats.compliant / stats.total) * 100) : 0;

  return (
    <section className="report-page">
      <div className="report-toolbar no-print">
        <button className="secondary report-action" onClick={() => navigate("/inspecoes")}>
          <ArrowLeft size={17} /> Voltar
        </button>
        <div className="report-toolbar-actions">
          <span className="report-hint"><FileDown size={16} /> Use “Salvar como PDF” na janela de impressão</span>
          <button className="primary report-action" onClick={() => window.print()}>
            <Printer size={17} /> Imprimir / Salvar PDF
          </button>
        </div>
      </div>

      <article className="report-sheet">
        <header className="report-header">
          <div>
            <img src="/mm-logo.svg" alt="MM Locações" className="report-logo" />
            <div className="report-title-block">
              <p>MM FLEET · GESTÃO E INSPEÇÃO DE FROTA</p>
              <h1>Relatório de Inspeção de Veículo / Equipamento</h1>
            </div>
          </div>
          <div className="report-code">
            <span>RELATÓRIO</span>
            <strong>{inspection.report_number}</strong>
            <small>{formatDateTime(inspection.created_at)}</small>
          </div>
        </header>

        <section className="report-summary">
          <div className="report-summary-main">
            <p className="report-section-label">IDENTIFICAÇÃO</p>
            <h2>{inspection.identifier}</h2>
            <p>{inspection.vehicle_type}{inspection.plate ? ` · Placa ${inspection.plate}` : ""}</p>
          </div>
          <div className={`report-result ${inspection.result}`}>
            <span>RESULTADO</span>
            <strong>{resultLabel[inspection.result]}</strong>
          </div>
        </section>

        <section className="report-info-grid">
          <div><span>Marca</span><strong>{inspection.brand || "—"}</strong></div>
          <div><span>Modelo</span><strong>{inspection.model || "—"}</strong></div>
          <div><span>Ano</span><strong>{inspection.year || "—"}</strong></div>
          <div>
            <span>{inspection.meter_type === "horimetro" ? "Horímetro da inspeção" : "KM da inspeção"}</span>
            <strong>
              {inspection.meter_value != null
                ? `${inspection.meter_value.toLocaleString("pt-BR")} ${inspection.meter_type === "horimetro" ? "h" : "km"}`
                : "Não se aplica / não informado"}
            </strong>
          </div>
          <div><span>Status operacional</span><strong>{operationalLabel[inspection.operational_status] || inspection.operational_status}</strong></div>
          <div><span>Unidade</span><strong>{inspection.unit_name || "Não definida"}</strong></div>
          <div><span>Responsável / motorista fixo</span><strong>{inspection.responsible_name || "—"}</strong></div>
          <div><span>Inspetor</span><strong>{inspection.inspector_name}</strong></div>
        </section>

        <section className="report-kpis">
          <div><strong>{stats.total}</strong><span>Itens avaliados</span></div>
          <div className="ok"><strong>{stats.compliant}</strong><span>Conformes</span></div>
          <div className="warn"><strong>{stats.warnings}</strong><span>Ressalvas</span></div>
          <div className="bad"><strong>{stats.noncompliant}</strong><span>Não conformes</span></div>
          <div className="rate"><strong>{conformity}%</strong><span>Conformidade</span></div>
        </section>

        <section className="report-traceability">
          <div>
            <span>DATA/HORA DE FINALIZAÇÃO</span>
            <strong>{formatDateTime(inspection.created_at)}</strong>
          </div>
          <div>
            <span>IDENTIFICADOR DO REGISTRO</span>
            <strong>{inspection.id}</strong>
          </div>
          <div>
            <span>ORIGEM DO HORÁRIO</span>
            <strong>Servidor / Cloudflare D1</strong>
          </div>
        </section>

        <section className="report-section">
          <div className="report-section-heading">
            <div>
              <p className="report-section-label">CHECKLIST</p>
              <h2>Itens verificados</h2>
            </div>
            <span>{answers.length} itens</span>
          </div>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Seção</th>
                  <th>Item verificado</th>
                  <th>Resultado</th>
                  <th>Gravidade</th>
                  <th>Observação</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((answer) => (
                  <tr key={answer.id}>
                    <td>{answer.section}</td>
                    <td>{answer.question}</td>
                    <td><span className={`answer-badge ${answer.status}`}>{answerLabel[answer.status]}</span></td>
                    <td><span className={`severity ${answer.severity}`}>{severityLabel[answer.severity] || answer.severity}</span></td>
                    <td>{answer.observation || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="report-section">
          <div className="report-section-heading">
            <div>
              <p className="report-section-label">NÃO CONFORMIDADES</p>
              <h2>Ocorrências geradas pela inspeção</h2>
            </div>
            <span>{nonconformities.length}</span>
          </div>
          {!nonconformities.length ? (
            <div className="report-clean">Nenhuma não conformidade foi gerada nesta inspeção.</div>
          ) : (
            <div className="report-nc-list">
              {nonconformities.map((nc, index) => (
                <article key={nc.id}>
                  <div className="report-nc-number">{String(index + 1).padStart(2, "0")}</div>
                  <div>
                    <strong>{nc.description}</strong>
                    <p>Responsável: {nc.responsible || "A definir"} · Prazo: {nc.deadline || "A definir"} · Status: {nc.status.replace("_", " ")}</p>
                  </div>
                  <span className={`severity ${nc.severity}`}>{severityLabel[nc.severity] || nc.severity}</span>
                </article>
              ))}
            </div>
          )}
        </section>

        {inspection.notes && (
          <section className="report-section">
            <p className="report-section-label">OBSERVAÇÕES GERAIS</p>
            <div className="report-notes">{inspection.notes}</div>
          </section>
        )}

        {photos.length > 0 && (
          <section className="report-section report-photo-section">
            <div className="report-section-heading">
              <div>
                <p className="report-section-label">EVIDÊNCIAS</p>
                <h2>Fotos da inspeção</h2>
              </div>
              <span>{photos.length} foto(s)</span>
            </div>
            <div className="report-photo-grid">
              {photos.map((photo, index) => (
                <figure key={photo.id}>
                  <img
                    src={photo.data_url || photo.url || ""}
                    alt={`Evidência ${index + 1}`}
                    loading="eager"
                  />
                  <figcaption>Evidência {index + 1}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <section className="report-section">
          <div className="report-section-heading">
            <div>
              <p className="report-section-label">RASTREABILIDADE</p>
              <h2>Registro de auditoria da inspeção</h2>
            </div>
          </div>
          <div className="report-audit">
            {audit.length ? audit.map((entry, index) => (
              <div key={index}>
                <strong>{entry.action.replaceAll("_", " ")}</strong>
                <span>{entry.user_name || "Sistema"} · {formatDateTime(entry.created_at)}</span>
              </div>
            )) : (
              <div><strong>Inspeção registrada</strong><span>{formatDateTime(inspection.created_at)}</span></div>
            )}
          </div>
          <p className="report-evidence-note">
            O registro é gerado no momento da finalização da inspeção. A data/hora é obtida do servidor, e o histórico operacional permanece preservado no sistema.
          </p>
        </section>

        <section className="report-signature-area">
          <div>
            <p className="report-section-label">RESPONSÁVEL PELA INSPEÇÃO</p>
            <strong>{inspection.inspector_name}</strong>
            <span>{inspection.inspector_email}</span>
          </div>
          <div className="report-signature">
            {signature ? <img src={signature.data_url} alt="Assinatura do inspetor" /> : <div className="signature-line" />}
            <span>Assinatura do inspetor</span>
          </div>
        </section>

        <footer className="report-footer">
          <div>Documento gerado pelo MM Fleet · Gestão e Inspeção de Frota · MM Locações</div>
          <div>{inspection.report_number}</div>
        </footer>
      </article>
    </section>
  );
}
